/**
 * Toolbar above the Tiptap markdown editor.
 *
 * Buttons cover simple markdown formatting: text size (paragraph + H1–H3),
 * bullets / numbered / task lists, indent / outdent, and the original
 * table controls. Bold / italic stay implicit via markdown shortcuts.
 *
 * Button presses use `onPointerDown` + `preventDefault` so the editor
 * never blurs — on iOS that would also dismiss the soft keyboard.
 */
import { isMacOS } from "@tiptap/core";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Columns2,
  Heading1,
  Heading2,
  Heading3,
  Indent,
  List,
  ListChecks,
  ListOrdered,
  ListTree,
  MessageSquarePlus,
  Outdent,
  Pilcrow,
  Quote,
  Rows2,
  SquareCode,
  Superscript,
  Table as TableIcon,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

interface EditorToolbarProps {
  editor: Editor;
  onCommentSelection?: (selection: MarkdownCommentSelection) => void;
  /**
   * When provided, render a table-of-contents toggle at the end of the bar.
   * Only supplied on narrow panes, where the TOC is a slide-down overlay
   * rather than a pinned sidebar (see MarkdownEditor).
   */
  tocToggle?: { open: boolean; onToggle: () => void };
}

export interface MarkdownCommentSelection {
  start: number;
  end: number;
  quote: string;
  display: string;
  source: string;
}

export function commentSelection(
  editor: Editor,
  from: number,
  to: number,
): MarkdownCommentSelection | null {
  const source = editor.getMarkdown();
  const marker = crypto.randomUUID().replaceAll("-", "").toUpperCase();
  const startMarker = `COMMENTSTART${marker}`;
  const endMarker = `COMMENTEND${marker}`;
  const marked = editor.markdown?.serialize(
    editor.state.tr
      .insertText(endMarker, to)
      .insertText(startMarker, from)
      .doc.toJSON(),
  );
  if (!marked) return null;
  const start = marked.indexOf(startMarker);
  const markedEnd = marked.indexOf(endMarker);
  if (start < 0 || markedEnd < start) return null;
  if (marked.replace(startMarker, "").replace(endMarker, "") !== source) {
    return null;
  }
  const end = markedEnd - startMarker.length;
  return {
    start,
    end,
    quote: source.slice(start, end),
    display: editor.state.doc.textBetween(from, to, "\n", "\n"),
    source,
  };
}

/** Start a comment on the editor's selection, or say why it can't be. */
export function commentOnSelection(
  editor: Editor,
  onCommentSelection: (selection: MarkdownCommentSelection) => void,
): void {
  const { from, to } = editor.state.selection;
  const selection = commentSelection(editor, from, to);
  if (selection?.quote.trim()) onCommentSelection(selection);
  else toast.error("Could not map this selection to the source text.");
}

const EMPTY_STATE = {
  inTable: false,
  canInsertTable: false,
  canMoveRowUp: false,
  canMoveRowDown: false,
  canMoveColumnLeft: false,
  canMoveColumnRight: false,
  isParagraph: false,
  isH1: false,
  isH2: false,
  isH3: false,
  isBulletList: false,
  isOrderedList: false,
  isTaskList: false,
  isTaskItem: false,
  isBlockquote: false,
  isCodeBlock: false,
  canInsertFootnote: false,
  canSinkTask: false,
  canSinkList: false,
  canLiftTask: false,
  canLiftList: false,
  hasSelection: false,
};

// The move shortcuts are worth naming in the tooltip, and macOS spells their
// modifiers with symbols. Each platform gets whole labels rather than a
// modifier fragment glued into a shared sentence.
const MOVE_LABELS = isMacOS()
  ? {
      rowUp: "Move row up (⌥⇧↑)",
      rowDown: "Move row down (⌥⇧↓)",
      columnLeft: "Move column left (⌥⇧←)",
      columnRight: "Move column right (⌥⇧→)",
    }
  : {
      rowUp: "Move row up (Alt+Shift+Up)",
      rowDown: "Move row down (Alt+Shift+Down)",
      columnLeft: "Move column left (Alt+Shift+Left)",
      columnRight: "Move column right (Alt+Shift+Right)",
    };

export function EditorToolbar({
  editor,
  tocToggle,
  onCommentSelection,
}: EditorToolbarProps) {
  const state = useEditorState({
    editor,
    // Guard against `editor.destroy()` having nulled internals (notably
    // `commandManager`) before the snapshot is invalidated. This can fire
    // during route transitions when tiptap's scheduled-destroy timer wins
    // the race with the mount effect — selectors keep running on the
    // destroyed instance until the subscription is torn down, and any
    // `.can()` call throws "reading 'can'" on the nulled commandManager.
    selector: ({ editor }) => {
      if (!editor || editor.isDestroyed) return EMPTY_STATE;
      return {
        inTable: editor.isActive("table"),
        canInsertTable: editor.can().insertTable({
          rows: 3,
          cols: 3,
          withHeaderRow: true,
        }),
        canMoveRowUp: editor.can().moveRowUp(),
        canMoveRowDown: editor.can().moveRowDown(),
        canMoveColumnLeft: editor.can().moveColumnLeft(),
        canMoveColumnRight: editor.can().moveColumnRight(),
        isParagraph: editor.isActive("paragraph"),
        isH1: editor.isActive("heading", { level: 1 }),
        isH2: editor.isActive("heading", { level: 2 }),
        isH3: editor.isActive("heading", { level: 3 }),
        isBulletList: editor.isActive("bulletList"),
        isOrderedList: editor.isActive("orderedList"),
        isTaskList: editor.isActive("taskList"),
        isTaskItem: editor.isActive("taskItem"),
        isBlockquote: editor.isActive("blockquote"),
        isCodeBlock: editor.isActive("codeBlock"),
        canInsertFootnote: editor.can().insertFootnote(),
        canSinkTask: editor.can().sinkListItem("taskItem"),
        canSinkList: editor.can().sinkListItem("listItem"),
        canLiftTask: editor.can().liftListItem("taskItem"),
        canLiftList: editor.can().liftListItem("listItem"),
        hasSelection: !editor.state.selection.empty,
      };
    },
  });

  const indent = () => {
    const name = state.isTaskItem ? "taskItem" : "listItem";
    editor.chain().focus().sinkListItem(name).run();
  };
  const outdent = () => {
    const name = state.isTaskItem ? "taskItem" : "listItem";
    editor.chain().focus().liftListItem(name).run();
  };

  return (
    // Outer bar spans the full pane width (border + background) and scrolls
    // horizontally when the controls can't fit. The inner row mirrors the
    // editor's 48rem content column (see MarkdownEditor) so the icons align
    // with the prose. The row holds its 48rem column when it fits, grows to
    // fit-content when the controls need more room, and falls back to full
    // width on narrow screens.
    <div className="shrink-0 overflow-x-auto border-b border-border bg-background py-1 text-muted-foreground">
      <div className="mx-auto flex w-fit min-w-[min(48rem,100%)] max-w-full items-center gap-1 px-2">
        <ToolbarButton
          label="Normal text"
          active={state.isParagraph}
          onClick={() => editor.chain().focus().setParagraph().run()}
        >
          <Pilcrow className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Heading 1"
          active={state.isH1}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Heading 2"
          active={state.isH2}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Heading 3"
          active={state.isH3}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          label="Bullet list"
          active={state.isBulletList}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={state.isOrderedList}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Task list"
          active={state.isTaskList}
          onClick={() => editor.chain().focus().toggleTaskList().run()}
        >
          <ListChecks className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Quote"
          active={state.isBlockquote}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Code block"
          active={state.isCodeBlock}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          <SquareCode className="h-4 w-4" />
        </ToolbarButton>
        {/* Drops `[^n]` at the cursor and an empty `[^n]: …` note at the end
            of the document, then moves the cursor into the note. */}
        <ToolbarButton
          label="Footnote"
          disabled={!state.canInsertFootnote}
          onClick={() => editor.chain().focus().insertFootnote().run()}
        >
          <Superscript className="h-4 w-4" />
        </ToolbarButton>
        {onCommentSelection ? (
          <ToolbarButton
            label="Comment on selection"
            disabled={!state.hasSelection}
            onClick={() => commentOnSelection(editor, onCommentSelection)}
          >
            <MessageSquarePlus className="h-4 w-4" />
          </ToolbarButton>
        ) : null}
        <Divider />
        <ToolbarButton
          label="Outdent"
          disabled={!state.canLiftTask && !state.canLiftList}
          onClick={outdent}
        >
          <Outdent className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Indent"
          disabled={!state.canSinkTask && !state.canSinkList}
          onClick={indent}
        >
          <Indent className="h-4 w-4" />
        </ToolbarButton>
        <Divider />
        {state.inTable ? (
          <>
            <ToolbarButton
              label="Add row below"
              onClick={() => editor.chain().focus().addRowAfter().run()}
            >
              <Rows2 className="h-4 w-4" />
              <span className="text-xs">+</span>
            </ToolbarButton>
            <ToolbarButton
              label="Add column right"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
            >
              <Columns2 className="h-4 w-4" />
              <span className="text-xs">+</span>
            </ToolbarButton>
            <ToolbarButton
              label={MOVE_LABELS.rowUp}
              disabled={!state.canMoveRowUp}
              onClick={() => editor.chain().focus().moveRowUp().run()}
            >
              <Rows2 className="h-4 w-4" />
              <ArrowUp className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              label={MOVE_LABELS.rowDown}
              disabled={!state.canMoveRowDown}
              onClick={() => editor.chain().focus().moveRowDown().run()}
            >
              <Rows2 className="h-4 w-4" />
              <ArrowDown className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              label={MOVE_LABELS.columnLeft}
              disabled={!state.canMoveColumnLeft}
              onClick={() => editor.chain().focus().moveColumnLeft().run()}
            >
              <Columns2 className="h-4 w-4" />
              <ArrowLeft className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              label={MOVE_LABELS.columnRight}
              disabled={!state.canMoveColumnRight}
              onClick={() => editor.chain().focus().moveColumnRight().run()}
            >
              <Columns2 className="h-4 w-4" />
              <ArrowRight className="h-3 w-3" />
            </ToolbarButton>
            <ToolbarButton
              label="Delete row"
              onClick={() => editor.chain().focus().deleteRow().run()}
            >
              <Rows2 className="h-4 w-4" />
              <span className="text-xs">−</span>
            </ToolbarButton>
            <ToolbarButton
              label="Delete column"
              onClick={() => editor.chain().focus().deleteColumn().run()}
            >
              <Columns2 className="h-4 w-4" />
              <span className="text-xs">−</span>
            </ToolbarButton>
            <ToolbarButton
              label="Delete table"
              onClick={() => editor.chain().focus().deleteTable().run()}
            >
              <Trash2 className="h-4 w-4" />
            </ToolbarButton>
          </>
        ) : (
          <ToolbarButton
            label="Insert table"
            disabled={!state.canInsertTable}
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
          >
            <TableIcon className="h-4 w-4" />
          </ToolbarButton>
        )}
        {tocToggle ? (
          // Just the last item in the ribbon after a divider — it flows with
          // the other controls, no right-alignment. Only shown on narrow panes:
          // the container query hides it (and its divider) at ≥ 992px, where
          // the TOC becomes a pinned sidebar. `active` gives it the
          // pressed/sticky look while the overlay is open.
          <>
            <div className="mx-1 h-4 w-px shrink-0 bg-border @min-[992px]:hidden" />
            <ToolbarButton
              label="Table of contents"
              active={tocToggle.open}
              onClick={tocToggle.onToggle}
              className="@min-[992px]:hidden"
            >
              <ListTree className="h-4 w-4" />
            </ToolbarButton>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="mx-1 h-4 w-px shrink-0 bg-border" />;
}

interface ToolbarButtonProps {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}

function ToolbarButton({
  label,
  active,
  disabled,
  onClick,
  className,
  children,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onPointerDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex shrink-0 items-center gap-1 rounded px-2 py-1.5 text-xs hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 ${
        active ? "bg-muted text-foreground" : ""
      } ${className ?? ""}`}
    >
      {children}
    </button>
  );
}
